/**
 * useChatTelemetry — fire-and-forget POST /chat/telemetry hook.
 *
 * PHASE_E §4.1. Wires the 4 frontend-emittable telemetry events:
 *   - follow_up_shown (ChatPanel.tsx)
 *   - follow_up_clicked (ChatFollowUpChips.tsx)
 *   - gap_q_resolved (ChatGapChip.tsx)
 *   - component_render_error (error boundary in ChatMessage.tsx)
 *
 * No-ops silently when:
 *   - There's no active sessionId (panel never opened a session yet)
 *   - The fetch fails (telemetry must never break the user-facing UX)
 *
 * The backend rejects backend-only event types via a Pydantic Literal,
 * so a typo here returns 422 — also caught silently by .catch(() => {}).
 */
import { useCallback } from 'react';

import { api } from '../../../api/client';
import { useChatStore } from '../../../stores/chatStore';

type FrontendEventType =
  | 'follow_up_shown'
  | 'follow_up_clicked'
  | 'gap_q_resolved'
  | 'component_render_error'
  // Citations Phase 1 — "View term sheet" link clicked below an assistant
  // message. Payload: { deal_id, term_sheet_id }.
  | 'term_sheet_link_clicked'
  // Citations Phase 2 — user clicked a footnote marker / list item.
  // Payload: { deal_id, citation_type }.
  | 'citation_clicked'
  // Contextual chip below an assistant message clicked. Payload: { chip_id, stage }.
  | 'contextual_chip_clicked';

export function useChatTelemetry() {
  const logEvent = useCallback(
    (eventType: FrontendEventType, payload: Record<string, unknown> = {}) => {
      const sessionId = useChatStore.getState().sessionId;
      if (!sessionId) {return;}

      const token = api.token.get();
      if (!token) {return;}

      // Fire-and-forget. Wrapped in setTimeout(0) so the network call
      // never delays the UI handler that triggered it (chip click,
      // render error, etc.).
      setTimeout(() => {
        fetch('/chat/telemetry', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_id: sessionId,
            event_type: eventType,
            payload,
          }),
        }).catch(() => {
          // Telemetry must never break the user-facing UX. Drop on the floor.
        });
      }, 0);
    },
    [],
  );

  return { logEvent };
}
